import { oklchToLinearRgb } from './oklch'

import type { Oklch } from './oklch'

/**
 * Gamut mapping para sRGB.
 *
 * OKLCH descreve cores que sRGB não consegue mostrar: um par (L, C) plausível
 * no papel pode exigir canal fora de [0,1]. O `oklchToHex` faz clamp por canal
 * como rede de segurança, mas clamp por canal distorce matiz e lightness — é
 * justamente o que essa camada existe para evitar.
 *
 * A estratégia é a padrão de CSS Color 4: segurar L e H e reduzir C até a cor
 * caber. Preservar lightness e matiz é o que mantém as garantias das camadas de
 * cima — o contraste resolvido por busca em L continua valendo, e o matiz do
 * evento não escorrega.
 */

/**
 * Tolerância por canal em sRGB linear. Existe para absorver o arredondamento
 * de ponto flutuante das matrizes, e nada além disso.
 *
 * A checagem é feita em linear, antes de qualquer quantização: conferir depois
 * do hex de 8 bits confunde erro de arredondamento com estouro de gamut, e o
 * mapeamento reage derrubando a croma de uma cor perfeitamente válida — um
 * azul vira cinza sem nenhum erro ser lançado.
 */
const EPS = 1e-6

/** Uma cor cabe em sRGB quando os três canais lineares ficam em [0, 1]. */
export const isInSrgb = (cor: Oklch): boolean =>
  oklchToLinearRgb(cor).every(canal => canal >= -EPS && canal <= 1 + EPS)

/** Passos da busca binária: 2^-18 em croma é bem abaixo de um passo de 8 bits. */
const PASSOS = 18

export const clampToSrgb = (cor: Oklch): Oklch => {
  if (isInSrgb(cor)) return cor

  // Croma zero sempre cabe (é a linha acromática), então o piso da busca é
  // seguro e a bissecção sempre converge.
  let baixo = 0
  let alto = cor.c

  for (let i = 0; i < PASSOS; i += 1) {
    const meio = (baixo + alto) / 2
    if (isInSrgb({ ...cor, c: meio })) baixo = meio
    else alto = meio
  }

  return { ...cor, c: baixo }
}
